// react_app/src/pages/admin/TranslationManagementPage.tsx

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import Layout from '../../components/Layout';
import { useTranslation } from '../../i18n/useTranslation';
import { translationApi, type LanguageDto } from '../../api/translationService';
import { TranslationsTab } from '../../components/admin/translations/TranslationsTab';
import { LanguagesTab } from '../../components/admin/translations/LanguagesTab';
import { useHasPermission } from '../../contexts/AbpPermissionsContext';

type Tab = 'translations' | 'languages';

export default function TranslationManagementPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>('translations');
  const [languages, setLanguages] = useState<LanguageDto[]>([]);
  const [loading, setLoading] = useState(true);
  const hasLanguagesPerm = useHasPermission('LanguageManagement.Languages');

  useEffect(() => {
    translationApi.getAllLanguages().then((data) => {
      setLanguages(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const tabs: { key: Tab; label: string; visible: boolean }[] = [
    { key: 'translations', label: t('App.Admin.Translations.TabTranslations'), visible: true },
    { key: 'languages', label: t('App.Admin.Translations.TabLanguages'), visible: hasLanguagesPerm },
  ];

  return (
    <Layout>
      <div className="container-app py-6">
        <h1 className="text-2xl font-bold text-neutral-900 mb-6">
          {t('App.Admin.Translations.PageTitle')}
        </h1>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-neutral-200">
          {tabs.filter((tab) => tab.visible).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-sm font-medium transition-colors min-h-[44px] border-b-2 -mb-px ${
                activeTab === tab.key
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
          </div>
        ) : (
          <>
            {activeTab === 'translations' && <TranslationsTab languages={languages} />}
            {activeTab === 'languages' && <LanguagesTab />}
          </>
        )}
      </div>
    </Layout>
  );
}
